import { useCallback, useEffect, useMemo, useState } from 'react'
import { Column } from 'react-table'
import { KTable, KTableHeader, KTableLoading } from '../../../../../../components/UI'
import { KTableProvider, useKTable } from '../../../../../../contexts/KTableProvider'
import { useLimpieza } from '../../../../../../hooks/useLimpieza'
import {
  getAllSquadMembers,
  updateLeaderCrewAssignment,
} from '../../../../services/crewAssignment.service'
import { ModalAddCrew } from '../modal/add-crew'
import { ShowSnackBar } from '../../../../../../util/functions'
import Swal from 'sweetalert2'
import { useFormContext } from 'react-hook-form'

const TableAddCrew = () => {
  const {
    data,
    setData,
    setPagination,
    loading,
    setLoading,
    pagination,
    clearSelected,
    selectedOn,
    clearTable,
    updateTable,
    setClearTable,
    setUpdateTable,
  } = useKTable()
  const { setValue } = useFormContext()
  const { changeCurrentTabItem, currentTabItem } = useLimpieza()
  const [showModalNew, setShowModalNew] = useState(false)
  const [squadId, setSquadId] = useState<string>('')

  const getCrew = async () => {
    try {
      setLoading(true)
      const { data: response } = await getAllSquadMembers()
      console.log(response)
      if (!response.error) {
        setData(response.data.members || [])
        setSquadId((response.data as any).squadId || '')
        setPagination({
          ...pagination,
          totalResults: 0,
        })
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error('Error al obtener miembros del squad:', error)
    }
  }

  useEffect(() => {
    // Llamar al servicio cuando el componente se monte o cuando cambien las dependencias
    if (currentTabItem.loadingTable || updateTable) {
      clearSelected()
      getCrew()
      setUpdateTable(false)
      setClearTable(false)
      changeCurrentTabItem({ ...currentTabItem, loadingTable: false })
    } else {
      // Cargar datos al montar el componente o cuando cambie la página
      getCrew()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, currentTabItem.loadingTable, updateTable])

  const handleNew = () => {
    setShowModalNew(true)
  }

  const handleLeaderChange = useCallback(
    async (row: any) => {
      const user = row.original
      const isLeader = user.esLider

      // Si ya es líder, no hacer nada (está bloqueado)
      if (isLeader) {
        return
      }

      // Mostrar modal de confirmación que espera la respuesta del servicio
      const result = await Swal.fire({
        title: `¿Estás seguro de configurar a ${user.nombreCompleto || user.Usuario} como líder?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí',
        cancelButtonText: 'No',
        confirmButtonColor: 'var(--kt-primary)',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async () => {
          try {
            const { data: response } = await updateLeaderCrewAssignment({
              squadId: squadId,
              leaderId: user.id || user.userId,
            })

            if (!response.error) {
              ShowSnackBar('Usuario configurado como líder exitosamente', 'success')
              // Actualizar la tabla
              setUpdateTable(true)
              return true // Indica que fue exitoso
            } else {
              Swal.showValidationMessage(response.message || 'Error al configurar el líder')
              ShowSnackBar(response.message || 'Error al configurar el líder', 'error')
              return false // Indica que hubo error
            }
          } catch (error) {
            Swal.showValidationMessage('Error al procesar la solicitud')
            ShowSnackBar('Error al procesar la solicitud', 'error')
            console.error('Error al actualizar líder:', error)
            return false // Indica que hubo error
          }
        },
      })

      // Si el usuario canceló o hubo error, no hacer nada más
      // El modal se cierra automáticamente solo si preConfirm retorna true
      if (!result.isConfirmed) {
        // El usuario canceló, no hacer nada
        return
      }
    },
    [setUpdateTable, squadId],
  )

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: (props) => <KTableHeader tableProps={props} title='Usuario' />,
        accessor: 'Usuario',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Nombres y Apellidos' />,
        accessor: 'nombreCompleto',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Lider' />,
        accessor: 'esLider',
        Cell: ({ row }: any) => {
          const user = row.original
          const isLeader = user.esLider

          return (
            <div className='d-flex justify-content-center'>
              <input
                type='radio'
                checked={isLeader}
                disabled={isLeader}
                onChange={(e) => {
                  e.preventDefault()
                  if (!isLeader) {
                    handleLeaderChange(row)
                  }
                }}
                style={{ cursor: isLeader ? 'not-allowed' : 'pointer' }}
              />
            </div>
          )
        },
      },
    ],
    [selectedOn, handleLeaderChange],
  )

  useEffect(() => {
    const crewUsers = Array.isArray(data) ? data : []
    const hasCrewUsers = crewUsers.length > 0
    const hasCrewLeader = crewUsers.some((user: any) => Boolean(user?.esLider))

    setValue('hasCrewUsers', hasCrewUsers)
    setValue('hasCrewLeader', hasCrewLeader)
  }, [data, setValue])

  return (
    <>
      <div className='d-flex justify-content-end pb-3'>
        <button type='button' className='btn btn-sm btn-success ' onClick={handleNew}>
          Agregar Usuario
        </button>
      </div>
      {loading ? <KTableLoading title='Cargando...' /> : null}
      <KTable
        data={data}
        columns={columns}
        paddingTable={false}
        showPagination={false}
        showButtons={false}
      />
      <ModalAddCrew
        show={showModalNew}
        setShow={setShowModalNew}
        onSuccess={() => setUpdateTable(true)}
      />
    </>
  )
}

export const AddCrewTable = () => {
  return (
    <KTableProvider>
      <TableAddCrew />
    </KTableProvider>
  )
}
